import { useQuery } from "@tanstack/react-query";
import { WorkspaceService } from "@gen/tracker/v1/workspace_connect";
import type { GetCallerResponse } from "@gen/tracker/v1/workspace_pb";

import { createClient } from "./connect";
import type { Scope } from "./period";

// Single source of truth for what the caller may do. The API resolves it
// (WorkspaceService.GetCaller): a realm customers token carries no tracker
// roles — they are the org's entitlements, and for our staff their realm vibe
// grant (S16). Every UI gate goes through this module; never read token
// claims for a role.

export interface TrackerRoles {
  /** tracker-admin | tracker-team-lead | tracker-member | tracker-viewer | "". */
  role: string;
  isPlatformAdmin: boolean;
  isTrackerAdmin: boolean;
  isEdgeOperator: boolean;
  canWrite: boolean;
  canManage: boolean;
  /**
   * «своё» (GetCaller.scope = "self"): the person sees only his own campaigns,
   * offers and their numbers — no other people, no «по байерам», only the
   * screens of his own work. The API enforces it; the UI hides the rest.
   */
  isSelf: boolean;
}

const NOBODY: TrackerRoles = {
  role: "",
  isPlatformAdmin: false,
  isTrackerAdmin: false,
  isEdgeOperator: false,
  canWrite: false,
  canManage: false,
  isSelf: false,
};

export const callerKey = ["caller"] as const;

const client = createClient(WorkspaceService);

/** The caller as the API resolved him; cached for the session. */
export function useCaller() {
  return useQuery({
    queryKey: callerKey,
    queryFn: () => client.getCaller({}),
    staleTime: 5 * 60_000,
  });
}

/** The roles of a GetCaller answer. Pure. */
export function rolesOf(caller: GetCallerResponse | undefined): TrackerRoles {
  if (!caller) return NOBODY;
  return {
    role: caller.role,
    isPlatformAdmin: caller.isPlatformAdmin,
    isTrackerAdmin: caller.isAdmin,
    isEdgeOperator: caller.isEdgeOperator,
    canWrite: caller.canWrite,
    canManage: caller.canManage,
    isSelf: caller.scope === "self",
  };
}

/**
 * Whose data a screen opens on when the address says nothing (the Top bar's
 * «Мои · Все»): a buyer (tracker-member) and a person in «своё» on their own,
 * everyone else on the whole workspace. While GetCaller is on its way the
 * narrower «Мои» — a buyer never sees the others' campaigns flash by. Pure.
 */
export function defaultScope(caller: GetCallerResponse | undefined, pending: boolean): Scope {
  if (pending) return "mine";
  return caller && (caller.role === "tracker-member" || caller.scope === "self") ? "mine" : "all";
}

/** «Мои · Все» when the address says nothing (`defaultScope`). */
export function useDefaultScope(): Scope {
  const caller = useCaller();
  return defaultScope(caller.data, caller.isPending);
}

/**
 * The caller's sub as the server knows him (GetCaller.sub): under console's
 * act-as the person acted as, not the admin signed in. Only for telling
 * «mine» on an answer (a badge, «can I edit it»); a request never names it —
 * «Мои» is `mine` on the request.
 */
export function useCallerSub(): string | undefined {
  return useCaller().data?.sub || undefined;
}

/** The caller's roles; nothing until GetCaller answers. */
export function useTrackerRoles(): TrackerRoles {
  return rolesOf(useCaller().data);
}

/** canWrite — a write role, or platform-admin. */
export function useCanWrite(): boolean {
  return useTrackerRoles().canWrite;
}
